import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { RaygunService } from 'src/app/raygun/raygun.service';
import { SessionStorage } from 'src/app/shared/models/clux/enum';
import { BrowserSessionStorageService } from 'src/app/shared/services/browser-session-storage.service';
import { TermsOfUseService } from 'src/app/terms-of-use/services/terms-of-use.service';

import { AuthenticationService } from '../services/authentication.service';
import { BrowserLocalStorageService } from '@app/shared/services/browser-local-storage.service';
import { Numbers } from '@app/shared/utils/numbers';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private authenticationService = inject(AuthenticationService);
  private browserSessionStorageService = inject(BrowserSessionStorageService);
  private raygunService = inject(RaygunService);
  private router = inject(Router);
  private termsOfUseService = inject(TermsOfUseService);
  private browserLocalStorageService = inject(BrowserLocalStorageService);

  private readonly MFA_PROMPT_SNOOZE_DURATION_IN_DAYS = 30;

  public async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    try {
      const validSession = await this.authenticationService.refreshSession();

      if (!validSession) {
        this.authenticationService.setSignInRedirect(state.url);
        this.browserSessionStorageService.set(SessionStorage.Url, state.url);
        await this.authenticationService.signOut(true);
        return this.router.parseUrl('/login');
      }

      const isMfaRequired = this.authenticationService.isMfaRequired();
      if (isMfaRequired) {
        return this.router.parseUrl('/login/enable-2fa');
      }

      const shouldNudgeOptionalMfa = await this.checkIfShouldNudgeOptionalMfa();
      if (shouldNudgeOptionalMfa) {
        return this.router.parseUrl('/login/enable-2fa');
      }

      const hasSignedTermsOfUse = await lastValueFrom(this.termsOfUseService.hasSignedTermsOfUse());
      if (!hasSignedTermsOfUse) {
        return this.router.parseUrl('/terms');
      }

      return true;
    } catch (error) {
      this.raygunService.logError('Error in auth.guard.ts:canActivate()', { error });
      return this.router.parseUrl('/logout');
    }
  }

  private async checkIfShouldNudgeOptionalMfa(): Promise<boolean> {
    const lastDismissed = this.browserLocalStorageService.getMfaNudgeSeenTimestamp();
    if (lastDismissed) {
      const snoozeDurationInMs = Numbers.minutesToMs(60 * 24 * this.MFA_PROMPT_SNOOZE_DURATION_IN_DAYS);
      if (Date.now() - lastDismissed < snoozeDurationInMs) {
        return false;
      }
    }

    const isAdditionalMfaOffered = await this.authenticationService.isMfaOfferedButNotRequired();
    if (!isAdditionalMfaOffered) {
      return false;
    }

    return true;
  }
}
